import { useGetQuery, type UseGetQueryOptions } from "@src/appV2/api";
import { environmentConfig } from "@src/appV2/environment";
import { APP_V2_APP_EVENTS } from "@src/appV2/lib";
import { type UseQueryResult } from "@tanstack/react-query";
import { z } from "zod";

const shiftSchema = z.object({
  _id: z.string(),
  start: z.string(),
  end: z.string(),
  facilityId: z.string(),
  agentReq: z.string(),
  agentId: z.string().optional(),
  facility: z
    .object({
      tmz: z.string().optional(),
    })
    .optional(),
  agent: z
    .object({
      name: z.string().optional(),
    })
    .optional(),
  clockInOut: z
    .object({
      start: z.string().datetime().optional(),
      end: z.string().datetime().optional(),
      clockInCreatedAt: z.string().datetime().optional(),
      clockOutCreatedAt: z.string().datetime().optional(),
      clockInComplianceProof: z.object({ bucketName: z.string(), fileKey: z.string() }).optional(),
      clockOutComplianceProof: z.object({ bucketName: z.string(), fileKey: z.string() }).optional(),
    })
    .optional(),
  lunchInOut: z
    .object({
      start: z.string().datetime().nullish(),
      end: z.string().datetime().nullish(),
    })
    .optional(),
  timecard: z
    .object({
      digitallySignedBy: z.string().optional(),
    })
    .optional(),
});

export const getShiftResponseSchema = z.object({
  success: z.boolean(),
  response: shiftSchema,
  error: z.unknown().nullable(),
});

export type GetShiftResponse = z.infer<typeof getShiftResponseSchema>;

export function getShiftPath(shiftId: string) {
  return `/v1/shifts/myShifts/details/${shiftId}`;
}

export function useGetShift(
  shiftId: string,
  options: UseGetQueryOptions<GetShiftResponse> & { customHeaders?: { Authorization: string } } = {}
): UseQueryResult<GetShiftResponse> {
  return useGetQuery({
    url: `${environmentConfig.REACT_APP_BASE_API_URL}${getShiftPath(shiftId)}`,
    responseSchema: getShiftResponseSchema,
    meta: {
      logErrorMessage: APP_V2_APP_EVENTS.GET_SHIFT_FAILURE,
    },
    ...options,
  });
}
